import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  GitBranch,
  ArrowRight,
  RefreshCw,
  CheckCircle,
  Clock,
  Eye,
  Loader,
  AlertCircle,
  Undo2,
  ExternalLink,
  Copy,
  Rocket,
  FileText,
  Shield
} from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

interface VersionInfo {
  publishedAt?: string
  publishedFrom?: string
  rolledBackAt?: string
  rolledBackFrom?: string
  backedUpAt?: string
  createdAt?: string
}

interface VersionStatus {
  live: VersionInfo | null
  preview: VersionInfo | null
  last: VersionInfo | null
  canPublish: boolean
  canRollback: boolean
}

interface DeployStatus {
  state: 'building' | 'ready' | 'error' | 'not_deployed' | 'unknown'
  url?: string
  errorMessage?: string
  createdAt?: string
}

interface ContentVersioningProps {
  onNavigateToTab?: (tab: 'documents' | 'features') => void
}

const ContentVersioning: React.FC<ContentVersioningProps> = ({ onNavigateToTab }) => {
  const { currentTenant } = useTenant()
  const navigate = useNavigate()
  const [status, setStatus] = useState<VersionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [rollingBack, setRollingBack] = useState(false)
  const [deployingPreview, setDeployingPreview] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null)
  const [deployStatus, setDeployStatus] = useState<{
    live?: DeployStatus
    preview?: DeployStatus
    last?: DeployStatus
  }>({})

  // Store interval IDs for cleanup on unmount
  const pollIntervalsRef = useRef<Set<NodeJS.Timeout>>(new Set())
  // Store timeout ID for copy to clipboard
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup all polling intervals and timeouts on unmount
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach(clearInterval)
      pollIntervalsRef.current.clear()
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  // Generate tenant URLs (Netlify branch deploy format)
  // Must match backend branch naming in netlify/functions/lib/github.mts:generateBranchName
  const generateTenantUrl = (version: 'live' | 'preview' | 'last'): string => {
    if (!currentTenant) return ''

    // Generate obfuscated suffix for uniqueness and security (must match backend)
    const generateObfuscatedSuffix = (tenantId: string, version: string): string => {
      const input = `${tenantId}-${version}`
      let hash = 0
      for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
      }
      return Math.abs(hash).toString().padStart(8, '0').substring(0, 8)
    }

    const obfuscatedSuffix = generateObfuscatedSuffix(currentTenant.id || '', version)
    const branchName = `${currentTenant.slug || 'tenant'}-${version}-${obfuscatedSuffix}`

    // Sanitize to match backend
    const sanitizedBranchName = branchName
      .replace(/\.+/g, '-')  // Replace dots with hyphens
      .replace(/[^a-zA-Z0-9\-]/g, '-')  // Replace invalid chars with hyphens
      .replace(/-+/g, '-')  // Remove consecutive hyphens
      .replace(/^-|-$/g, '')  // Remove leading/trailing hyphens

    return `${sanitizedBranchName}.trustcenterpage.com`
  }

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(`https://${url}`)
      setCopiedUrl(url)
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedUrl(null)
        copyTimeoutRef.current = null
      }, 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  useEffect(() => {
    loadStatus()
    // Check deploy status for all versions on load
    checkDeployStatus('live')
    checkDeployStatus('preview')
    checkDeployStatus('last')
  }, [])

  const checkDeployStatus = async (version: 'live' | 'preview' | 'last', preserveBuilding = false) => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch(`/api/deploy-status?version=${version}`, {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        let finalState = data.state

        // If we want to preserve 'building' state, use functional setState to check current state
        if (preserveBuilding) {
          setDeployStatus(prev => {
            const currentState = prev[version]?.state

            if (currentState === 'building') {
              console.log(`🔍 ${version} preserveBuilding mode - current: building, API returned: ${data.state}`)

              // Only update if we get a definitive terminal state or another building state
              if (data.state === 'building') {
                // Keep it building, update other fields
                console.log(`⏳ ${version} keeping building state`)
                finalState = 'building'
                return {
                  ...prev,
                  [version]: {
                    state: 'building',
                    url: data.url,
                    errorMessage: data.errorMessage,
                    createdAt: data.createdAt
                  }
                }
              } else if (data.state === 'ready' || data.state === 'error') {
                // Deployment finished - update to final state
                console.log(`✅ ${version} deployment finished: ${data.state}`)
                finalState = data.state
                return {
                  ...prev,
                  [version]: {
                    state: data.state,
                    url: data.url,
                    errorMessage: data.errorMessage,
                    createdAt: data.createdAt
                  }
                }
              } else {
                // Any other state (not_deployed, unknown) - keep building state
                console.log(`⏳ ${version} still building, ignoring API state: ${data.state}`)
                finalState = 'building'
                return prev // Don't update at all, keep building
              }
            }

            // Not in building state, just update normally
            return {
              ...prev,
              [version]: {
                state: data.state,
                url: data.url,
                errorMessage: data.errorMessage,
                createdAt: data.createdAt
              }
            }
          })
        } else {
          // Normal update (not preserving building)
          setDeployStatus(prev => ({
            ...prev,
            [version]: {
              state: data.state,
              url: data.url,
              errorMessage: data.errorMessage,
              createdAt: data.createdAt
            }
          }))
        }

        return finalState
      } else {
        console.error(`❌ ${version} deploy status API error:`, response.status)
      }
    } catch (error) {
      console.error(`Failed to check ${version} deploy status:`, error)
    }

    return null
  }

  const pollDeployStatus = async (version: 'live' | 'preview' | 'last', maxAttempts = 60) => {
    let attempts = 0

    const checkAndContinue = async () => {
      attempts++
      // Use preserveBuilding flag to prevent overwriting "building" state with stale data
      const state = await checkDeployStatus(version, true)

      if (state === 'ready' || state === 'error' || attempts >= maxAttempts) {
        if (state === 'ready') {
          console.log(`✅ ${version} deploy completed successfully`)
        } else if (state === 'error') {
          console.error(`❌ ${version} deploy failed`)
        } else if (attempts >= maxAttempts) {
          console.warn(`⚠️ ${version} deploy polling timed out after ${maxAttempts} attempts`)
        }
        return true // Stop polling
      }
      return false // Continue polling
    }

    // Start polling immediately, then every 5 seconds
    console.log(`🔄 Starting ${version} deploy status polling...`)

    // First check immediately
    const shouldStop = await checkAndContinue()
    if (shouldStop) return

    // Then poll every 5 seconds (faster polling for better UX)
    const pollInterval = setInterval(async () => {
      const shouldStop = await checkAndContinue()
      if (shouldStop) {
        clearInterval(pollInterval)
        pollIntervalsRef.current.delete(pollInterval)
      }
    }, 5000)

    // Store interval ID for cleanup on unmount
    pollIntervalsRef.current.add(pollInterval)
  }

  const loadStatus = async () => {
    try {
      setLoading(true)

      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/content-versioning?action=status', {
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        setStatus(data)
      } else {
        console.error('Failed to load version status')
      }
    } catch (error) {
      console.error('Failed to load version status:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePublish = async () => {
    if (!confirm('Publish preview content to live? This will make your changes visible to all visitors.')) {
      return
    }

    setPublishing(true)

    // Show immediate deploying status when user clicks OK
    setDeployStatus(prev => ({
      ...prev,
      live: {
        state: 'building',
        url: undefined,
        errorMessage: undefined
      },
      last: {
        state: 'building',
        url: undefined,
        errorMessage: undefined
      }
    }))

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/content-versioning?action=publish', {
        method: 'POST',
        credentials: 'include',
        headers
      })

      const result = await response.json()

      if (result.success) {
        alert('Content changes saved! Your site will deploy automatically in 2-3 minutes.')

        // Reload content version status but keep the building deploy status
        await loadStatus()

        // Start polling for deploy status - keep 'building' state visible
        // Poll both Live (new publish) and Last (backup) deployments
        pollDeployStatus('live')
        pollDeployStatus('last')
      } else {
        alert(`Failed to publish: ${result.error}`)
      }
    } catch (error) {
      console.error('Publish error:', error)
      alert(`Failed to publish: ${error.message}`)
    } finally {
      setPublishing(false)
    }
  }

  const handleRollback = async () => {
    if (!confirm('Rollback to previous version? This will restore the last published content.')) {
      return
    }

    setRollingBack(true)

    // Show immediate deploying status when user clicks OK
    setDeployStatus(prev => ({
      ...prev,
      live: {
        state: 'building',
        url: undefined,
        errorMessage: undefined
      }
    }))

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/content-versioning?action=rollback', {
        method: 'POST',
        credentials: 'include',
        headers
      })

      const result = await response.json()

      if (result.success) {
        alert('Content rolled back! Your site will redeploy automatically in 2-3 minutes.')

        // Reload content version status but keep the building deploy status
        await loadStatus()

        // Start polling for Live deploy status - keep 'building' state visible
        pollDeployStatus('live')
      } else {
        alert(`Failed to rollback: ${result.error}`)
      }
    } catch (error) {
      console.error('Rollback error:', error)
      alert(`Failed to rollback: ${error.message}`)
    } finally {
      setRollingBack(false)
    }
  }

  const handleDeployPreview = async () => {
    if (!confirm('Deploy your preview content? This will create a preview site you can share with your team.')) {
      return
    }

    setDeployingPreview(true)

    // Show immediate deploying status when user clicks OK
    setDeployStatus(prev => ({
      ...prev,
      preview: {
        state: 'building',
        url: undefined,
        errorMessage: undefined
      }
    }))

    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/content-versioning?action=deploy-preview', {
        method: 'POST',
        credentials: 'include',
        headers
      })

      const result = await response.json()

      if (result.success) {
        alert('Preview deploy triggered! Your preview site is now deploying...')

        // Reload content version status but keep the building deploy status
        await loadStatus()

        // Start polling for deploy status - keep 'building' state visible
        pollDeployStatus('preview')
      } else {
        alert(`Failed to deploy preview: ${result.error}`)
      }
    } catch (error) {
      console.error('Deploy preview error:', error)
      alert(`Failed to deploy preview: ${error.message}`)
    } finally {
      setDeployingPreview(false)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString()
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 animate-spin text-primary-600" />
          <span className="ml-2 text-gray-600">Loading version status...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Versioning</h3>
        <p className="text-sm text-gray-600">
          Manage your trust center content versions. Preview changes before publishing.
        </p>
      </div>

      {/* Step 1: Add/Edit Content */}
      <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-2">Step 1: Add / Edit Content</h4>
            <p className="text-sm text-gray-600">
              Add documents to your evidence library and configure security features. Changes are automatically saved to your preview version.
            </p>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button
              onClick={() => {
                if (onNavigateToTab) {
                  onNavigateToTab('documents')
                  // Scroll to the documents section after tab switch
                  setTimeout(() => {
                    const element = document.getElementById('documents')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }, 100)
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors whitespace-nowrap"
            >
              <FileText className="h-4 w-4" />
              Add Documents
            </button>
            <button
              onClick={() => {
                if (onNavigateToTab) {
                  onNavigateToTab('features')
                  // Scroll to the features section after tab switch
                  setTimeout(() => {
                    const element = document.getElementById('features')
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  }, 100)
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors whitespace-nowrap"
            >
              <Shield className="h-4 w-4" />
              Add Features
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        {/* Step 2: Optional Preview Deploy */}
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-blue-900 mb-1">Step 2: Test Your Changes (Optional)</h4>
              <p className="text-sm text-blue-700 mb-3">
                Deploy to a preview site to test your changes before publishing to the public live site. Skip this if you're confident in your changes.
              </p>

              {/* Preview URL */}
              {(() => {
                const previewVersion = deployStatus.preview
                const previewUrl = generateTenantUrl('preview')

                return (
                  <div className="mt-3 space-y-2">
                    <code className="text-xs text-blue-900 bg-blue-100 px-2 py-1 rounded block">{previewUrl}</code>
                    {previewVersion?.createdAt && previewVersion?.state === 'ready' && (
                      <p className="text-xs text-blue-700">
                        Deployed: {new Date(previewVersion.createdAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
            <div className="flex flex-col items-end gap-2 ml-4">
              {/* Status Badge */}
              {(() => {
                const previewVersion = deployStatus.preview
                if (!previewVersion) return null

                return (
                  <div className={`flex items-center justify-center w-full px-4 py-2 rounded-md text-sm font-medium ${
                    previewVersion.state === 'building' ? 'bg-yellow-100 text-yellow-800' :
                    previewVersion.state === 'ready' ? 'bg-green-100 text-green-800' :
                    previewVersion.state === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {previewVersion.state === 'building' && (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Deploying
                      </>
                    )}
                    {previewVersion.state === 'ready' && (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Live
                      </>
                    )}
                    {previewVersion.state === 'error' && (
                      <>
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Error
                      </>
                    )}
                    {previewVersion.state === 'not_deployed' && 'Not Deployed'}
                  </div>
                )
              })()}

              {/* Action Buttons */}
              {(() => {
                const previewUrl = generateTenantUrl('preview')

                return (
                  <>
                    <button
                      onClick={handleDeployPreview}
                      disabled={!status?.canPublish || deployingPreview}
                      className="flex items-center justify-center w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      data-tour="deploy-preview-btn"
                    >
                      {deployingPreview ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          Deploying...
                        </>
                      ) : (
                        <>
                          <Eye className="h-4 w-4 mr-2" />
                          Deploy Preview
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(previewUrl)}
                      className="flex items-center justify-center w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium"
                    >
                      {copiedUrl === previewUrl ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy URL
                        </>
                      )}
                    </button>
                    <a
                      href={`https://${previewUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open URL
                    </a>
                  </>
                )
              })()}
            </div>
          </div>
        </div>

        {/* Step 3: Publish to Live */}
        <div className="border border-green-200 rounded-lg p-4 bg-green-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 mb-1">Step 3: Publish to Live</h4>
              <p className="text-sm text-green-700 mb-3">
                Make your preview changes visible to all public visitors. This backs up your current live version first, so you can rollback if needed.
              </p>
              {/* Live URL */}
              {(() => {
                const liveVersion = deployStatus.live
                const liveUrl = generateTenantUrl('live')

                return (
                  <div className="mt-3 space-y-2">
                    <code className="text-xs text-green-900 bg-green-100 px-2 py-1 rounded block">{liveUrl}</code>
                    {liveVersion?.createdAt && liveVersion?.state === 'ready' && (
                      <p className="text-xs text-green-700">
                        Deployed: {new Date(liveVersion.createdAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
            <div className="flex flex-col items-end gap-2 ml-4">
              {/* Status Badge */}
              {(() => {
                const liveVersion = deployStatus.live
                if (!liveVersion) return null

                return (
                  <div className={`flex items-center justify-center w-full px-4 py-2 rounded-md text-sm font-medium ${
                    liveVersion.state === 'building' ? 'bg-yellow-100 text-yellow-800' :
                    liveVersion.state === 'ready' ? 'bg-green-100 text-green-800' :
                    liveVersion.state === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {liveVersion.state === 'building' && (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Deploying
                      </>
                    )}
                    {liveVersion.state === 'ready' && (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Live
                      </>
                    )}
                    {liveVersion.state === 'error' && (
                      <>
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Error
                      </>
                    )}
                    {liveVersion.state === 'not_deployed' && 'Not Deployed'}
                  </div>
                )
              })()}

              {/* Action Buttons */}
              {(() => {
                const liveUrl = generateTenantUrl('live')

                return (
                  <>
                    <button
                      onClick={handlePublish}
                      disabled={!status?.canPublish || publishing}
                      className="flex items-center justify-center w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      data-tour="publish-live-btn"
                    >
                      {publishing ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          Publish to Live
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(liveUrl)}
                      className="flex items-center justify-center w-full px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm font-medium"
                    >
                      {copiedUrl === liveUrl ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy URL
                        </>
                      )}
                    </button>
                    <a
                      href={`https://${liveUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full px-4 py-2 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open URL
                    </a>
                  </>
                )
              })()}
            </div>
          </div>
        </div>

        {/* Step 4: Rollback to Last */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">Step 4: Rollback to Last</h4>
              <p className="text-sm text-gray-700 mb-3">
                Need to undo? Rollback will restore your previous live version. This is your backup from before the last publish.
              </p>
              {/* Last URL */}
              {(() => {
                const lastVersion = deployStatus.last
                const lastUrl = generateTenantUrl('last')

                return (
                  <div className="mt-3 space-y-2">
                    <code className="text-xs text-gray-900 bg-gray-100 px-2 py-1 rounded block">{lastUrl}</code>
                    {lastVersion?.createdAt && lastVersion?.state === 'ready' && (
                      <p className="text-xs text-gray-700">
                        Deployed: {new Date(lastVersion.createdAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                )
              })()}
            </div>
            <div className="flex flex-col items-end gap-2 ml-4">
              {/* Status Badge */}
              {(() => {
                const lastVersion = deployStatus.last
                if (!lastVersion) return null

                return (
                  <div className={`flex items-center justify-center w-full px-4 py-2 rounded-md text-sm font-medium ${
                    lastVersion.state === 'building' ? 'bg-yellow-100 text-yellow-800' :
                    lastVersion.state === 'ready' ? 'bg-gray-100 text-gray-800' :
                    lastVersion.state === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {lastVersion.state === 'building' && (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Deploying
                      </>
                    )}
                    {lastVersion.state === 'ready' && (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Ready
                      </>
                    )}
                    {lastVersion.state === 'error' && (
                      <>
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Error
                      </>
                    )}
                    {lastVersion.state === 'not_deployed' && 'Not Deployed'}
                  </div>
                )
              })()}

              {/* Action Buttons */}
              {(() => {
                const lastUrl = generateTenantUrl('last')

                return (
                  <>
                    <button
                      onClick={handleRollback}
                      disabled={!status?.canRollback || rollingBack}
                      className="flex items-center justify-center w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      {rollingBack ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          Rolling back...
                        </>
                      ) : (
                        <>
                          <Undo2 className="h-4 w-4 mr-2" />
                          Rollback to Last
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(lastUrl)}
                      className="flex items-center justify-center w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                    >
                      {copiedUrl === lastUrl ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy URL
                        </>
                      )}
                    </button>
                    <a
                      href={`https://${lastUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open URL
                    </a>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold mb-2">Important Notes:</p>
            <ul className="space-y-1">
              <li>• <strong>Deploy Preview is optional</strong> - Use it to test before going live, or skip straight to publishing</li>
              <li>• <strong>Publish to Live makes changes public</strong> - This is what your visitors will see</li>
              <li>• <strong>Live version is automatically backed up</strong> - You can rollback anytime if something goes wrong</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentVersioning
